import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import {Tooltip, Button} from 'react-bootstrap';
import {
  LayoutDashboard,
  UserRoundCog,
  ClipboardCheck,
  CalendarDays,
  Building2,
  UserRound,
  LogOut,
  Settings,
  ChevronLeft,
  ChevronRight,
  Megaphone,
  CreditCard,
} from 'lucide-react';

const iconProps = { size: 20, strokeWidth: 2, className: 'flex-shrink-0', style: { width: 24, height: 24 } };

const STORAGE_KEYS = {
  super: 'super-admin-sidebar-collapsed',
  institutional: 'inst-admin-sidebar-collapsed',
};

const SuperAdminSidebar = ({ className = '', variant = 'super' }) => {
  const location = useLocation();
  const storageKey = STORAGE_KEYS[variant] || STORAGE_KEYS.super;
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    const stored = localStorage.getItem(storageKey);
    return stored === 'true';
  });

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(storageKey, String(next));
      return next;
    });
  };

  const isActive = (path) => location.pathname === path;

  const isInstitutional = variant === 'institutional';

  const navItems = isInstitutional
    ? [
        { path: '/institutional-admin/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
        { path: '/institutional-admin/user-program-management', label: 'program management', Icon: UserRoundCog },
        { path: '/institutional-admin/calendar', label: 'Calendar', Icon: CalendarDays },
        { path: '/institutional-admin/evaluations', label: 'Evaluations', Icon: ClipboardCheck },
        { path: '/institutional-admin/announcements', label: 'Announcements', Icon: Megaphone },
        { path: '/institutional-admin/payments-subscriptions', label: 'Billing', Icon: CreditCard },
      ]
    : [
        { path: '/super/dashboard', label: 'Dashboard', Icon: LayoutDashboard },
        { path: '/super/accounts', label: 'Account Management', Icon: Building2 },
        { path: '/dashboard', label: 'Directors View', Icon: UserRound },
      ];

  const headerLabel = isInstitutional ? 'Inst. Admin' : 'Super Admin';

  return (
    <aside
      className={`super-sidebar ${sidebarCollapsed ? 'super-sidebar-collapsed' : ''} ${className}`}
      style={{ width: sidebarCollapsed ?  56 : 240 }}
    >
      <div className={`d-flex align-items-center px-3 py-3 border-bottom border-secondary ${sidebarCollapsed ? 'justify-content-center' : ''}`} style={{ height: '61px' }}>
        <img src={sidebarCollapsed ? '/images/asset11.svg' : '/images/asset4.svg'} alt="Logo" className="flex-shrink-0" style={{ height: sidebarCollapsed ? 36 : 14 }} />
        {!sidebarCollapsed && <span className="ms-2 fw-semibold text-nowrap">{headerLabel}</span>}
      </div>
      <nav className="mt-1 justify-content-center">
        {navItems.map(({ path, label, Icon }) => {
          const tipId = `super-sidebar-nav-${path.replace(/\//g, '-').replace(/^-/, '')}`;
          const linkClass = `${sidebarCollapsed ? 'justify-content-center' : ''} ${isActive(path) ? 'active' : ''}`.trim();
          const linkBody = (
            <>
              <Icon {...iconProps} aria-hidden />
              {!sidebarCollapsed && <span className="ms-2">{label}</span>}
            </>
          );
          return (
            <React.Fragment key={path}>
              {sidebarCollapsed ? (
                <OverlayTrigger
                  placement="right"
                  delay={{ show: 250, hide: 80 }}
                  overlay={<Tooltip id={tipId}>{label}</Tooltip>}
                >
                  <Button
                    variant="link"
                    href={path}
                    className={linkClass}
                    style={{ minHeight: 44, textDecoration: 'none' }}
                  >
                    {linkBody}
                  </Button>
                </OverlayTrigger>
              ) : (
                <Button
                  variant="link"
                  href={path}
                  className={linkClass}
                  style={{ minHeight: 44, textDecoration: 'none' }}
                >
                  {linkBody}
                </Button>
              )}
            </React.Fragment>
          );
        })}
      </nav>
 
      
        {isInstitutional && (
          <>
            {sidebarCollapsed ? (
              <OverlayTrigger
                placement="right"
                delay={{ show: 250, hide: 80 }}
                overlay={<Tooltip id="super-sidebar-settings">Settings</Tooltip>}
              >
                <Button
                  variant="link"
                  href="/institutional-admin/settings"
                  className={`d-flex align-items-center w-100 p-3 rounded-0 text-white text-decoration-none p-0 border-0 justify-content-center ${isActive('/institutional-admin/settings') ? 'active' : ''}`}
                  style={{ minHeight: 44 }}
                >
                  <Settings {...iconProps} aria-hidden />
                </Button>
              </OverlayTrigger>
            ) : (
              <Button
                variant="link"
                href="/institutional-admin/settings"
                className={`d-flex align-items-center w-100 p-3 rounded-0 text-white text-decoration-none p-0 border-0 text-start ${isActive('/institutional-admin/settings') ? 'active' : ''}`}
                style={{ minHeight: 44 }}
              >
                <Settings {...iconProps} aria-hidden />
                <span className="ms-2">Settings</span>
              </Button>
            )}
          </>
        )}
        <div className="border-top border-bottom border-secondary">
        {sidebarCollapsed ? (
          <OverlayTrigger
            placement="right"
            delay={{ show: 250, hide: 80 }}
            overlay={<Tooltip id="super-sidebar-logout">Logout</Tooltip>}
          >
            <Button
              variant="link"
              className="d-flex align-items-center w-100 p-3 rounded-0 text-white text-decoration-none p-0 border-0 justify-content-center"
              onClick={() => { localStorage.clear(); window.location.replace('/'); }}
              style={{ minHeight: 44 }}
            >
              <LogOut {...iconProps} aria-hidden />
            </Button>
          </OverlayTrigger>
        ) : (
          <Button
            variant="link"
            className="d-flex align-items-center w-100 p-3 rounded-0 text-white text-decoration-none p-0 border-0 text-start"
            onClick={() => { localStorage.clear(); window.location.replace('/'); }}
            style={{ minHeight: 44 }}
          >
            <LogOut {...iconProps} aria-hidden />
            <span className="ms-2">Logout</span>
          </Button>
        )}
      </div>
      <Button
        variant="dark"
        className="rounded-0 d-flex align-items-center justify-content-center py-2"
        onClick={toggleSidebar}
        aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
      >
        {sidebarCollapsed ? (
          <ChevronRight size={18} strokeWidth={2} aria-hidden />
        ) : (
          <ChevronLeft size={18} strokeWidth={2} aria-hidden />
        )}
      </Button>
    </aside>
  );
};

export default SuperAdminSidebar;
